import * as React from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

type TranslationValue = string | TranslationMap
type TranslationMap = { [key: string]: TranslationValue }

type TranslationModule = { default: TranslationMap } | TranslationMap

type I18nContextValue = {
  language: string
  setLanguage: (code: string) => void
  t: (key: string, fallback?: string) => string
  isLoading: boolean
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined)

const translationModules = import.meta.glob('../languages/**/*.json') as Record<
  string,
  () => Promise<TranslationModule>
>

function getValueByPath(
  object: TranslationMap,
  path: string,
): string | undefined {
  const segments = path.split('.')
  let current: TranslationValue | undefined = object
  for (const segment of segments) {
    if (typeof current !== 'object' || current === null)
      return undefined
    current = (current as TranslationMap)[segment]
    if (current === undefined)
      return undefined
  }
  return typeof current === 'string' ? current : undefined
}

function deepMerge(
  target: TranslationMap,
  source: TranslationMap,
): TranslationMap {
  const result: TranslationMap = { ...target }
  for (const key of Object.keys(source)) {
    const targetValue = result[key]
    const sourceValue = source[key]
    if (sourceValue === undefined) {
      continue
    }
    result[key]
      = typeof targetValue === 'object'
        && targetValue !== null
        && typeof sourceValue === 'object'
        && sourceValue !== null
        ? deepMerge(
            targetValue as TranslationMap,
            sourceValue as TranslationMap,
          )
        : sourceValue
  }
  return result
}

async function loadLanguageTranslations(
  languageCode: string,
): Promise<TranslationMap> {
  let merged: TranslationMap = {}
  const loaders = Object.entries(translationModules)
    .filter(([path]) => path.includes(`/languages/${languageCode}/`))
    .map(([, loader]) => loader)

  for (const load of loaders) {
    const mod = await load()
    const data: TranslationMap
      = 'default' in mod
        ? (mod as { default: TranslationMap }).default
        : (mod as TranslationMap)
    merged = deepMerge(merged, data)
  }

  return merged
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<string>(() => {
    const stored
      = typeof window !== 'undefined' ? localStorage.getItem('lang') : null
    return stored || 'en'
  })
  const [messages, setMessages] = useState<TranslationMap>({})
  const [isLoading, setIsLoading] = useState<boolean>(true)

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    loadLanguageTranslations(language)
      .then((data) => {
        if (cancelled)
          return
        setMessages(data)
      })
      .catch(() => {
        if (cancelled)
          return
        setMessages({})
      })
      .finally(() => {
        if (cancelled)
          return
        setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [language])

  const setLanguage = useCallback((code: string) => {
    setLanguageState(code)
    try {
      localStorage.setItem('lang', code)
    }
    catch {}
  }, [])

  const t = useCallback(
    (key: string, fallback?: string) => {
      const value = getValueByPath(messages, key)
      return value !== undefined ? value : (fallback ?? key)
    },
    [messages],
  )

  const value = useMemo<I18nContextValue>(
    () => ({ language, setLanguage, t, isLoading }),
    [language, setLanguage, t, isLoading],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n() {
  const ctx = useContext(I18nContext)
  if (!ctx) {
    throw new Error('useI18n must be used within an I18nProvider')
  }
  return ctx
}
